import { ABHA_GENDER_TO_FORM } from '../../utils/gender';

/**
 * toABHAProfileSnapshot — normalizes any ABHA flow output into a single
 * canonical shape. Creation, verification, and scan-share all emit
 * slightly different raw objects; this is the single mapping layer.
 *
 * Returns a frozen, immutable snapshot so consumers can't mutate it.
 *
 * @param {object} raw   — raw output from any ABHA flow
 * @param {string} source — 'creation' | 'verification' | 'scan_share'
 * @returns {ABHAProfileSnapshot | null}
 */
export function toABHAProfileSnapshot(raw, source) {
  if (!raw) return null;

  const snapshot = {
    abhaNumber:    raw.abhaNumber    ?? raw.abha_number    ?? null,
    abhaAddress:   raw.abhaAddress   ?? raw.abha_address   ?? null,
    fullName:      raw.fullName      ?? raw.name           ?? null,
    gender:        raw.gender        ?? null,
    yearOfBirth:   raw.yearOfBirth   ?? raw.year_of_birth  ?? null,
    mobile:        raw.mobile        ?? null,
    // Full profile (present on the verify response) — carried so link can persist the ABDM
    // snapshot + enrich patient demographics.
    firstName:        raw.firstName        ?? raw.first_name        ?? null,
    middleName:       raw.middleName       ?? raw.middle_name       ?? null,
    lastName:         raw.lastName         ?? raw.last_name         ?? null,
    dob:              raw.dob              ?? raw.dateOfBirth ?? raw.date_of_birth ?? null,
    profilePhoto:     raw.profilePhoto     ?? raw.profile_photo     ?? null,
    email:            raw.email            ?? null,
    addressLine:      raw.addressLine      ?? raw.address_line      ?? raw.address ?? null,
    stateName:        raw.stateName        ?? raw.state_name        ?? null,
    districtName:     raw.districtName     ?? raw.district_name     ?? null,
    subdistrictName:  raw.subdistrictName  ?? raw.subdistrict_name  ?? null,
    townName:         raw.townName         ?? raw.town_name         ?? null,
    pincode:          raw.pincode          ?? null,
    verificationType: raw.verificationType ?? raw.verification_type ?? null,
    kycVerified:      raw.kycVerified      ?? raw.kyc_verified      ?? null,
    aadhaar:          raw.aadhaar          ?? null,   // create flow only; fills the form's Aadhaar field
    source,
    verifiedAt:    raw.verifiedAt    ?? new Date().toISOString(),
    // Healthcare-worker + beneficiary names captured with the consent (creation flow only).
    consentWorkerName:      raw.consentWorkerName      ?? null,
    consentBeneficiaryName: raw.consentBeneficiaryName ?? null,
    // The mobile the user typed in the ABHA flow — the register form uses it to skip its mobile step.
    registrationMobile:     raw.registrationMobile     ?? null,
    correlationId: raw.correlationId ?? raw.correlation_id ?? null,
  };

  return Object.freeze(snapshot);
}

/**
 * snapshotLookupMobile — the 10-digit mobile an ABHA snapshot offers for the patient lookup, or ''.
 * Single source for the OPD and admission forms: the mobile typed in the ABHA flow
 * (registrationMobile: creation, scan) else the profile mobile (verify). A masked value
 * (X / * characters) or anything that is not exactly 10 digits after trimming a +91/0 prefix is ignored.
 */
export function snapshotLookupMobile(snapshot) {
  for (const raw of [snapshot?.registrationMobile, snapshot?.mobile]) {
    const s = String(raw || '');
    if (!s || /[xX*•]/.test(s)) continue;
    const digits = s.replace(/\D/g, '').slice(-10);
    if (digits.length === 10) return digits;
  }
  return '';
}

/**
 * buildAbhaLinkRequest — decide whether an ABHA completion should be persisted
 * to a patient, and with what payload. Returns the linkABHAToPatient() args, or
 * `null` when a link MUST NOT be attempted (no patient, or no ABHA number).
 *
 * This is the guard whose absence caused the "verified but never saved" bug: the
 * Book Appointment modal flipped the badge locally and never called the link API,
 * so `abha_status` reverted to NOT_LINKED on the next patient search.
 *
 * recordVersion is always 0 — the backend link_abha retries once and re-reads the
 * fresh record_version on conflict, so 0 is safe even for a re-verify of an
 * already-LINKED patient.
 *
 * @param {string} patientId
 * @param {ABHAProfileSnapshot | null} snapshot — from toABHAProfileSnapshot()
 * @returns {{ patientId, abhaNumber, abhaAddress, recordVersion } | null}
 */
// A masked/display-only ABHA number carries X/x/* (e.g. 91-XXXX-XXXX-2124). It must never be
// sent to the link API — the backend enforces the same invariant, this just avoids a doomed call.
export function isMaskedAbhaNumber(value) {
  return Boolean(value) && /[^0-9\- ]/.test(value);
}

// ── Display masking (presentation only — the full values are stored server-side) ──

/** Mask an ABHA number for display: 14-digit → "91-XXXX-XXXX-1234"; else keep only the last 4. */
export function maskAbhaNumber(value) {
  if (!value) return '';
  if (isMaskedAbhaNumber(value)) return value;         // already masked
  const d = String(value).replace(/\D/g, '');
  if (d.length === 14) return `${d.slice(0, 2)}-XXXX-XXXX-${d.slice(-4)}`;
  return d.length > 4 ? `···${d.slice(-4)}` : value;
}

/** Mask an ABHA address for display: keep the first 2 chars of the handle + the domain
 *  ("shivangdhuria2507@sbx" → "sh***@sbx"). Non-@ values are returned unchanged. */
export function maskAbhaAddress(value) {
  if (!value || typeof value !== 'string' || !value.includes('@')) return value || '';
  const [local, domain] = value.split('@');
  const head = local.slice(0, 2);
  return `${head}${'*'.repeat(3)}@${domain}`;
}

export function buildAbhaLinkRequest(patientId, snapshot) {
  if (!patientId) return null;
  const abhaNumber = snapshot?.abhaNumber;
  if (!abhaNumber || isMaskedAbhaNumber(abhaNumber)) return null;   // never link a masked number
  // The photo is display-only (the server never persists it) — don't ship it back over the wire.
  const { profilePhoto: _photo, ...profile } = snapshot;
  return {
    patientId,
    abhaNumber,
    abhaAddress: snapshot?.abhaAddress || '',
    recordVersion: 0,
    profile,   // full verified profile → persisted + demographics enrichment
  };
}

// ABHA gender code (M/F/O) → the patient forms' gender select values.
export { ABHA_GENDER_TO_FORM };  // single definition lives in utils/gender

/**
 * R139 — the ONE ABHA-name → form-name rule (mirrors the backend link_guard.abha_names).
 * With an ABHA first name (verify): First = first + middle, Last = last name (may be '').
 * Without one (ABHA-create carries only fullName + middleName, and fullName already includes the middle):
 * split fullName — all words but the last / the last word. A middle name alone is never the first name.
 */
export function abhaNameParts(snapshot) {
  const join = (...xs) => xs.map((x) => String(x || '').trim()).filter(Boolean).join(' ');
  if (!snapshot) return { first: '', last: '' };
  const lastName = String(snapshot.lastName || '').trim();
  if (String(snapshot.firstName || '').trim()) return { first: join(snapshot.firstName, snapshot.middleName), last: lastName };
  const words = String(snapshot.fullName || '').trim().split(/\s+/).filter(Boolean);
  if (!words.length) return { first: '', last: lastName };
  return { first: words.slice(0, -1).join(' ') || words[0], last: words.length > 1 ? words[words.length - 1] : '' };
}

/** Last name is optional only for an attached one-word ABHA name that the create payload can vouch for:
 *  an unmasked abha_number or a scan_id (the server allows an empty last name only then). */
export const isLastNameRequired = (snapshot) => {
  const { first, last } = abhaNameParts(snapshot);
  const sendable = Boolean(snapshot?.scanId) || Boolean(snapshot?.abhaNumber && !isMaskedAbhaNumber(snapshot.abhaNumber));
  return !(first && !last && sendable);
};

/** First-name input is locked to the ABHA name only when the snapshot actually has one (else staff type it). */
export const isFirstNameLocked = (snapshot) => Boolean(abhaNameParts(snapshot).first);

/** Last-name input is locked to the ABHA name, except when the ABHA has no last name but one is still required (staff type it). */
export const isLastNameLocked = (snapshot) =>
  Boolean(snapshot) && !(isLastNameRequired(snapshot) && !abhaNameParts(snapshot).last);

/** Full patient name for display: trims, no 'undefined', no trailing space for an empty last name. */
export const formatPatientName = (p) =>
  [p?.first_name, p?.last_name].map((x) => String(x || '').trim()).filter(Boolean).join(' ');

/**
 * abhaAutofillPatch — given a verified ABHA snapshot and the CURRENT form values, return the
 * subset of demographic fields to set. Fills only EMPTY fields (never overwrites what the user
 * typed / what an existing patient already has). Shared by the Book Appointment and Register
 * Visit new-patient flows so both auto-fill identically.
 *
 * @param {ABHAProfileSnapshot|null} snapshot
 * @param {object} form — current form values (first_name/last_name/dob/gender/age)
 * @returns {object} patch to merge into the form
 */
export function abhaAutofillPatch(snapshot, form = {}) {
  if (!snapshot) return {};
  const patch = {};
  const fill = (key, val) => { if (val && !form[key]) patch[key] = val; };

  // R139: the ABHA name is authoritative (the name inputs are read-only while the ABHA is attached).
  const { first, last } = abhaNameParts(snapshot);
  if (first) { patch.first_name = first; patch.last_name = last; }
  fill('dob', snapshot.dob);                               // ABHA dob is ISO YYYY-MM-DD
  fill('gender', ABHA_GENDER_TO_FORM[snapshot.gender]);
  fill('aadhaar_no', String(snapshot.aadhaar || '').replace(/\D/g, '').slice(0, 12));
  if (!form.age && snapshot.yearOfBirth) {
    const age = new Date().getFullYear() - Number(snapshot.yearOfBirth);
    if (age > 0) patch.age = String(age);
  }
  return patch;
}

// Backend refuses a second SELF patient on one mobile; shown inline next to Relation.
export const SELF_TAKEN_COPY = "This number already has a patient marked 'Self'. Choose how this person is related (for example Wife, Child, Parent or Other).";
export const SELF_TAKEN_CODE = 'SELF_ALREADY_EXISTS';
// Structured code first (appointments 409); message regex is the fallback (visit registration plain message).
export const isSelfTakenError = (err) =>
  err?.code === SELF_TAKEN_CODE || err?.data?.detail?.code === SELF_TAKEN_CODE ||
  /SELF patient is already registered/i.test(String(err?.message || err?.data?.detail?.message || err?.data?.detail || ''));
